import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Copy, Download, RefreshCw, AlertTriangle, ArrowLeft, Target as TargetIcon, Users, Wallet, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import BriefBody from "@/components/BriefBody";
import { fetchBrief } from "@/lib/api";
import { briefToPlainText } from "@/lib/format";

export default function BriefPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [brief, setBrief] = useState(location.state?.brief || null);
  const [loading, setLoading] = useState(!brief);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (brief) return;
    let alive = true;
    (async () => {
      try {
        const data = await fetchBrief(id);
        if (alive) setBrief(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Brief not found");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, brief]);

  const handleCopy = async () => {
    if (!brief) return;
    try {
      await navigator.clipboard.writeText(briefToPlainText(brief));
      toast.success("Brief copied to clipboard");
    } catch {
      toast.error("Copy failed — try Download PDF instead");
    }
  };

  const handlePdf = async () => {
    if (!printRef.current) return;
    const html2pdf = (await import("html2pdf.js")).default;
    const opt = {
      margin: [12, 12, 12, 12],
      filename: `CompetitorPulse-${brief.target_company.replace(/\s+/g, "_")}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };
    toast.message("Preparing PDF…");
    await html2pdf().set(opt).from(printRef.current).save();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-slate-500 font-mono text-sm tracking-wider uppercase">Loading brief…</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <SiteHeader />
        <main className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
          <p className="text-slate-700">{error || "Brief not found."}</p>
          <Button onClick={() => navigate("/")} className="bg-electric text-white hover:bg-[#1448D8]">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to home
          </Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />

      {/* Toolbar */}
      <div className="border-b border-slate-200 bg-white sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono tracking-widest uppercase text-slate-500">Competitive Brief</div>
            <h1 data-testid="brief-header-title" className="font-display text-xl sm:text-2xl font-bold text-navy tracking-tight truncate">
              {brief.target_company}
              {brief.user_product && <span className="text-slate-400 font-normal"> vs {brief.user_product}</span>}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleCopy}
              data-testid="copy-brief-button"
              className="border-slate-300 text-navy hover:bg-slate-50"
            >
              <Copy className="mr-2 h-4 w-4" /> Copy brief
            </Button>
            <Button
              variant="outline"
              onClick={handlePdf}
              data-testid="download-pdf-button"
              className="border-slate-300 text-navy hover:bg-slate-50"
            >
              <Download className="mr-2 h-4 w-4" /> Download PDF
            </Button>
            <Button
              onClick={() => navigate("/")}
              data-testid="generate-another-button"
              className="bg-electric text-white hover:bg-[#1448D8]"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Generate another
            </Button>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div
          data-testid="ai-disclaimer-note"
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>AI-generated analysis — verify before use.</span>
        </div>
      </div>

      {/* Brief content (this ref is what gets exported to PDF) */}
      <motion.main
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1"
      >
        <div ref={printRef} className="bg-white">
          <BriefBody brief={brief} />
        </div>
      </motion.main>

      <SiteFooter />
    </div>
  );
}
